import React, { FC, useEffect, useState } from 'react';
import { Button, ButtonProps } from 'reactstrap';
import { TranslationKey, useTranslation } from '../translations';
import { classNames, shouldDisableHotkeys } from '../utils';

import './HotkeyButton.scss';

interface HotkeyButtonProps extends ButtonProps {
  readonly actionText: TranslationKey;
  readonly hotkey: string;
  readonly onClick: () => Promise<void> | void;
  readonly requiredKeyPressCount?: number;
  readonly bypassDisableHotkeys?: boolean;
}

const convertKeyToText = (key: string) => {
  switch (key) {
    case ' ': {
      return 'SPACE BAR';
    }

    default: {
      return key.toUpperCase();
    }
  }
};

export const HotkeyButton: FC<HotkeyButtonProps> = ({
  actionText,
  hotkey,
  requiredKeyPressCount = 1,
  onClick,
  children,
  bypassDisableHotkeys = false,
  ...buttonProps
}) => {
  const [keyPressCount, setKeyPressCount] = useState(0);
  const remainingKeyPresses = requiredKeyPressCount - keyPressCount;
  const { ts } = useTranslation();

  useEffect(() => {
    const resetTimer = setTimeout(() => {
      setKeyPressCount(0);
    }, 2000);

    const handleKeyDown = async (event: KeyboardEvent) => {
      if ((!bypassDisableHotkeys && shouldDisableHotkeys()) || buttonProps.disabled) {
        return;
      }

      if (event.key === hotkey) {
        if (keyPressCount + 1 !== requiredKeyPressCount) {
          setKeyPressCount(keyPressCount + 1);
          return;
        }

        clearTimeout(resetTimer);
        await onClick();
        setKeyPressCount(0);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      clearTimeout(resetTimer);
    };
  }, [
    keyPressCount,
    requiredKeyPressCount,
    onClick,
    hotkey,
    buttonProps.disabled,
    bypassDisableHotkeys,
  ]);

  const isHotkeyDisabled =
    buttonProps.disabled || (!bypassDisableHotkeys && shouldDisableHotkeys());

  return (
    <div className={classNames({ 'w-100': buttonProps.block }, 'hotkey-button')}>
      <Button {...buttonProps} onClick={onClick}>
        {children}
      </Button>
      {!isHotkeyDisabled && (
        <div className="hotkey-explanation">
          {ts('cashier.or_press')} <strong>{convertKeyToText(hotkey)}</strong>{' '}
          {remainingKeyPresses > 1 ? `${remainingKeyPresses} times` : '1 time'} to {ts(actionText)}.
        </div>
      )}
    </div>
  );
};
